import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";

// data: flat object keyed by field.key. onChange(key, value)
export function SchemaForm({ schema, data, onChange }) {
  const get = (k) => data[k];
  return (
    <div className="space-y-8">
      {schema.sections.map((section) => (
        <div key={section.id} data-testid={`section-${schema.type}-${section.id}`}>
          <h4 className="mb-4 font-heading text-base font-bold tracking-tight text-brand">{section.title}</h4>
          <div className="space-y-4">
            {section.fields.map((f) => (
              <Field key={f.key} f={f} value={get(f.key)} onChange={(v) => onChange(f.key, v)} type={schema.type} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ f, value, onChange, type }) {
  const tid = `f-${type}-${f.key}`;
  if (f.type === "text" || f.type === "date" || f.type === "time") {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">{f.label}</Label>
        <Input data-testid={tid} type={f.type === "text" ? "text" : f.type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        {f.note && <p className="text-xs text-muted-foreground">{f.note}</p>}
      </div>
    );
  }
  if (f.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">{f.label}</Label>
        <Textarea data-testid={tid} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (f.type === "radio") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-secondary/40 px-3 py-2.5">
        <div>
          <Label className="text-sm font-medium">{f.label}</Label>
          {f.note && <p className="text-xs text-muted-foreground">{f.note}</p>}
        </div>
        <div className="flex gap-1.5">
          {f.options.map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`${tid}-${opt.replace(/\W/g, "")}`}
              onClick={() => onChange(opt)}
              className={`rounded-md border px-3 py-1 text-sm font-semibold transition-colors ${
                value === opt ? "border-brand bg-brand text-white" : "border-border bg-white text-foreground hover:bg-secondary"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === "confirm") {
    return (
      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-secondary/50">
        <Checkbox data-testid={tid} checked={!!value} onCheckedChange={(c) => onChange(!!c)} className="mt-0.5" />
        <span className="text-sm leading-snug">{f.label}</span>
      </label>
    );
  }
  if (f.type === "matrix") {
    const val = value || {};
    const toggle = (row, col) => {
      const next = { ...(val[row] || {}) };
      next[col] = !next[col];
      onChange({ ...val, [row]: next });
    };
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold">{f.label}</Label>
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Item</th>
                {f.columns.map((c) => (
                  <th key={c} className="w-20 px-2 py-2 text-center font-semibold">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {f.rows.map((r) => (
                <tr key={r.label} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.label}</div>
                    {r.desc && <div className="text-xs text-muted-foreground">{r.desc}</div>}
                  </td>
                  {f.columns.map((c) => (
                    <td key={c} className="px-2 py-2 text-center">
                      <Checkbox
                        data-testid={`${tid}-${r.label.replace(/\W/g, "").slice(0, 12)}-${c.replace(/\W/g, "")}`}
                        checked={!!(val[r.label] && val[r.label][c])}
                        onCheckedChange={() => toggle(r.label, c)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return null;
}
